import { MediaVideo, MediaLink } from "@/components/admin/MediaAsset";
import { useEffect, useState } from "react";
import { Loader2, Trash2, Check, Video, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { VideoUploadButton, fileUrl } from "@/components/admin/VideoUploader";

const VideoCard = ({ m, picker, onPick, onDelete, readOnly }) => {
  const url = fileUrl(m.storage_path);
  const date = new Date(m.created_at).toLocaleDateString("id-ID", { dateStyle: "medium" });
  return (
    <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-xl overflow-hidden hover:border-[#3B82F6] transition-colors" data-testid={`video-item-${m.id}`}>
      <MediaVideo src={url} preload="metadata" controls={!picker} muted playsInline className="w-full h-auto max-h-80 object-contain bg-black" />
      <div className="p-2 space-y-1.5">
        <p className="text-xs font-medium text-slate-800 truncate" title={m.original_filename}>{m.original_filename || "video.mp4"}</p>
        <p className="text-[10px] text-slate-500">{(m.size / 1048576).toFixed(1)}MB · {date}</p>
        {picker ? (
          <Button size="sm" onClick={() => onPick({ url, name: m.original_filename || "video.mp4", size: m.size })} data-testid={`video-pick-${m.id}`} className="w-full h-8 bg-[#2563EB] hover:bg-[#1D4ED8] rounded-lg text-xs">
            <Check size={13} className="mr-1" /> Pakai Video
          </Button>
        ) : (
          <div className="flex gap-1.5">
            <MediaLink href={url} target="_blank" rel="noreferrer" aria-label="Buka video asli" title="Buka video asli" data-testid={`video-original-${m.id}`} className="h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-100"><ExternalLink size={12} /></MediaLink>
            <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(url); toast.success("URL video disalin"); }} data-testid={`video-copy-${m.id}`} className="flex-1 h-8 rounded-lg text-xs"><Copy size={12} className="mr-1" /> URL</Button>
            {!readOnly && <Button size="sm" variant="outline" onClick={() => onDelete(m)} data-testid={`video-delete-${m.id}`} className="h-8 rounded-lg text-xs text-[#E11D48] hover:bg-[#FFF1F2]"><Trash2 size={12} /></Button>}
          </div>
        )}
      </div>
    </div>
  );
};

export default function VideoGallery({ picker = false, onPick, readOnly = false }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { setVideos((await api.get("/media", { params: { kind: "video" } })).data || []); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (m) => {
    if (!window.confirm(`Hapus video "${m.original_filename || "video"}"?`)) return;
    try { await api.delete(`/media/${m.id}`); toast.success("Video dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="video-gallery">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <span className="text-sm text-slate-500" data-testid="video-gallery-count">{videos.length} video</span>
        {!picker && !readOnly && <div className="flex flex-wrap items-center gap-2 max-w-md justify-end"><VideoUploadButton onUploaded={load} /></div>}
      </div>
      {loading ? (
        <div className="text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Memuat...</div>
      ) : videos.length === 0 ? (
        <div className="bg-[#FFFFFF] border border-dashed border-[#E2E8F0] rounded-2xl p-12 text-center" data-testid="video-gallery-empty">
          <Video size={32} className="mx-auto text-slate-600 mb-3" />
          <p className="text-slate-600">{readOnly ? "Belum ada video milik Anda." : "Belum ada video. Upload video MP4 (maks. 100MB) dari sini atau dari Generate Konten."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-4">
          {videos.map((m) => <VideoCard key={m.id} m={m} picker={picker} onPick={onPick} onDelete={remove} readOnly={readOnly} />)}
        </div>
      )}
    </div>
  );
}
